import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Receipt, TrendingUp, TrendingDown, Calculator } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const TaxReport = () => {
  const [report, setReport] = useState({
    total_income: 0,
    total_expenses: 0,
    total_salary: 0,
    taxable_income: 0,
    estimated_tax: 0
  });
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let totalIncome = 0;
      let totalExpenses = 0;
      let totalSalary = 0;

      // Fetch incomes - use pagination stats for accurate total
      try {
        const incomesRes = await apiClient.get('/api/accounts/incomes?page=1&limit=1');
        totalIncome = incomesRes.pagination?.stats?.total_amount || 0;
      } catch (e) {
        console.log('Income fetch error:', e);
      }

      // Fetch expenses - use pagination stats for accurate total
      try {
        const expensesRes = await apiClient.get('/api/accounts/expenses?page=1&limit=1');
        totalExpenses = expensesRes.pagination?.stats?.total_amount || 0;
      } catch (e) {
        console.log('Expense fetch error:', e);
      }

      // Fetch salaries
      try {
        const salariesRes = await apiClient.get('/api/accounts/salary-payments');
        const salaries = Array.isArray(salariesRes.data) ? salariesRes.data : [];
        totalSalary = salaries.reduce((sum, s) => sum + (s.net_salary || 0), 0);
      } catch (e) {
        console.log('Salary fetch error:', e);
      }

      const taxableIncome = totalIncome - totalExpenses - totalSalary;
      const estimatedTax = taxableIncome > 0 ? taxableIncome * 0.30 : 0;

      setReport({
        total_income: totalIncome,
        total_expenses: totalExpenses,
        total_salary: totalSalary,
        taxable_income: taxableIncome,
        estimated_tax: estimatedTax
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" />
            Tax Report - FY {year}-{year + 1}
          </CardTitle>
          <div className="flex gap-2 items-center">
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="w-24" />
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Total Income</p>
                    <p className="text-2xl font-bold text-green-700">₹{report.total_income.toLocaleString('en-IN')}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Total Deductions</p>
                    <p className="text-2xl font-bold text-red-700">₹{(report.total_expenses + report.total_salary).toLocaleString('en-IN')}</p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-indigo-50 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600">Taxable Income</p>
                    <p className="text-2xl font-bold text-indigo-700">₹{Math.max(0, report.taxable_income).toLocaleString('en-IN')}</p>
                  </div>
                  <Calculator className="w-8 h-8 text-indigo-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tax Calculation Summary */}
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-indigo-600" />
                Tax Calculation Summary
              </h3>
              <table className="w-full">
                <tbody>
                  <tr className="border-b">
                    <td className="py-3">Gross Income</td>
                    <td className="text-right font-medium text-green-600">₹{report.total_income.toLocaleString('en-IN')}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3">(-) Operating Expenses</td>
                    <td className="text-right text-red-600">₹{report.total_expenses.toLocaleString('en-IN')}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3">(-) Salary Expenses</td>
                    <td className="text-right text-red-600">₹{report.total_salary.toLocaleString('en-IN')}</td>
                  </tr>
                  <tr className="border-b font-semibold bg-gray-50">
                    <td className="py-3">Net Taxable Income</td>
                    <td className="text-right text-indigo-700">₹{Math.max(0, report.taxable_income).toLocaleString('en-IN')}</td>
                  </tr>
                  <tr className="bg-indigo-100">
                    <td className="py-4 font-bold text-lg">Estimated Tax (30%)</td>
                    <td className="text-right text-2xl font-bold text-indigo-700">₹{report.estimated_tax.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Note */}
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This is an estimated tax calculation based on current financial data. 
              Please consult a tax professional for accurate tax filing.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxReport;
